import { createContext, ReactNode, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ViewAddonsRenderer, { Sidebar, SidebarEnum, SidebarType, ViewAddonsRef } from "./ViewAddonsRenderer";
import ViewAddonsSidebar from "./ViewAddonsSidebar";

interface Props {
    children: ReactNode;
    setOpenDrawer: (open: boolean) => void;
}

interface ViewAddonsContext {
    setExtra: ViewAddonsRef['setExtra'];
    setContent: ViewAddonsRef['setContent'];
    setSidebars: ViewAddonsRef['setSidebars'];
    reset: ViewAddonsRef['reset'];
    setSidebarType: ViewAddonsRef['setSidebarType'];
}

interface ViewAddonsSubscribeContext {
    subscribe?: ViewAddonsRef['subscribe'];
    setSidebarType?: ViewAddonsRef['setSidebarType'];
}

// eslint-disable-next-line react-refresh/only-export-components
export const viewAddonsContext = createContext<ViewAddonsContext>({
    reset: () => { return; },
    setSidebarType: () => { return; },
    setExtra: () => { return; },
    setSidebars: () => { return; },
    setContent: () => { return; }
});

// eslint-disable-next-line react-refresh/only-export-components
export const viewAddonsSubscribeContext = createContext<ViewAddonsSubscribeContext>({
    subscribe: () => () => { return; },
    setSidebarType: () => { return; }
});

const ViewAddons = (props: Props) => {
    const renderRef = useRef<ViewAddonsRef>(null);
    const [isReady, setIsReady] = useState(false);

    useLayoutEffect(() => {
        if (renderRef.current)
            setIsReady(true);
    }, []);

    const viewAddonsProviderInit: ViewAddonsContext = useMemo(() => ({
        reset: () => renderRef.current?.reset(),
        setSidebarType: (type) => renderRef.current?.setSidebarType(type),
        setExtra: (content) => renderRef.current?.setExtra(content),
        setSidebars: (content) => renderRef.current?.setSidebars(content),
        setContent: (content) => renderRef.current?.setContent(content)
    }), []);

    return (
        <>
            <ViewAddonsRenderer ref={renderRef} setOpenDrawer={props.setOpenDrawer} />

            <viewAddonsContext.Provider
                value={viewAddonsProviderInit}
            >
                {
                    isReady ?
                        <div className='__viewaddons-content'>
                            <div className='__viewaddons-content-container'>
                                {props.children}
                            </div>
                            <viewAddonsSubscribeContext.Provider
                                value={{
                                    subscribe: renderRef.current?.subscribe,
                                    setSidebarType: renderRef.current?.setSidebarType
                                }}
                            >
                                <ViewAddonsSidebar />
                            </viewAddonsSubscribeContext.Provider>
                        </div>
                        : null
                }
            </viewAddonsContext.Provider>
        </>
    );
};

interface ViewAddonsSidebarsHook {
    sidebarType: SidebarType,
    sidebars: Sidebar[];
    setSidebarType: ViewAddonsRef['setSidebarType'];
}

// eslint-disable-next-line react-refresh/only-export-components
export const useViewAddonsSidebars = (): ViewAddonsSidebarsHook => {
    const context = useContext(viewAddonsSubscribeContext);
    const [sidebarType, setSidebarType] = useState<SidebarType>(SidebarEnum.NONE);
    const [sidebars, setSidebars] = useState<Sidebar[]>([]);

    const subscription = useRef<() => void>();

    if (!context)
        throw new Error('useViewAddons must be used within an ViewAddonsProvider');

    useEffect(() => {
        if (context.subscribe)
            subscription.current = context.subscribe((type, tabSidebars) => {
                setSidebarType(type);
                setSidebars(tabSidebars);
            });

        return () => {
            if (subscription.current)
                subscription.current();
        };
    }, [context, setSidebarType, setSidebars]);

    return {
        sidebarType,
        sidebars,
        setSidebarType: (type) => {
            setSidebarType(type);
            context.setSidebarType?.(type);
        }
    };
};

// eslint-disable-next-line react-refresh/only-export-components
export const useViewAddons = (): ViewAddonsContext => {
    const context = useContext(viewAddonsContext);

    if (!context)
        throw new Error('useViewAddons must be used within an ViewAddonsProvider');

    useLayoutEffect(() => {
        return () => {
            if (context)
                context.reset();
        };
    }, [context]);

    return context;
};

export default ViewAddons;